export type CloudConversationStartupPhase =
  | 'repository'
  | 'task-configuration'
  | 'authorization'
  | 'sandbox-request'
  | 'repository-credentials'
  | 'sandbox-runtime'
  | 'repository-checkout'
  | 'finalizing'
  | 'connecting'
  | 'initializing'
  | 'opening-session'
  | 'error';

export type CloudConversationStartupStep = {
  id: Exclude<CloudConversationStartupPhase, 'error'>;
  label: string;
  detail: string;
};

export function formatStartupDuration(durationMs: number): string {
  const seconds = Math.max(0, durationMs) / 1_000;
  return seconds < 10 ? `${seconds.toFixed(1)}s` : `${Math.round(seconds)}s`;
}

export function cloudConversationStartupSteps(
  hasRepository: boolean,
): readonly CloudConversationStartupStep[] {
  return [
    ...(hasRepository
      ? ([
          {
            id: 'repository',
            label: 'Configure repository',
            detail: 'Save the selected repository, branch, and base commit.',
          },
        ] satisfies CloudConversationStartupStep[])
      : []),
    {
      id: 'task-configuration',
      label: 'Load task configuration',
      detail: 'Create or read the cloud task and its runtime settings.',
    },
    {
      id: 'authorization',
      label: 'Authorize cloud access',
      detail: 'Resolve the user, organization, and cloud permissions.',
    },
    {
      id: 'sandbox-request',
      label: 'Create or resume sandbox',
      detail: 'Ask the Kubernetes control plane to provision the sandbox.',
    },
    ...(hasRepository
      ? ([
          {
            id: 'repository-credentials',
            label: 'Issue repository credentials',
            detail: 'Create a short-lived source control installation token.',
          },
          {
            id: 'sandbox-runtime',
            label: 'Wait for sandbox runtime',
            detail: 'Wait for the pod, image, network, and workspace service to become ready.',
          },
          {
            id: 'repository-checkout',
            label: 'Restore repository',
            detail: 'Clone the base commit and apply the saved workspace patch.',
          },
        ] satisfies CloudConversationStartupStep[])
      : []),
    {
      id: 'finalizing',
      label: 'Save runtime state',
      detail: 'Persist the sandbox name, workspace path, and running status.',
    },
    {
      id: 'connecting',
      label: 'Connect to ACP',
      detail: 'Open the browser-to-sandbox WebSocket connection.',
    },
    {
      id: 'initializing',
      label: 'Initialize agent',
      detail: 'Negotiate ACP capabilities and initialize the selected agent.',
    },
    {
      id: 'opening-session',
      label: 'Open conversation',
      detail: 'Create or resume the agent session for this conversation.',
    },
  ];
}
